﻿import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Heading, Text, Tooltip } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { modulesService } from '../../services/modules'
import { supabase } from '../../lib/supabase'
import { serverInfoService, PM2Process, NginxProject } from '../../services/serverInfo'
import { 
  UserIcon,
  ExclamationTriangleIcon,
  ServerIcon,
  FolderIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowPathIcon,
  TableCellsIcon,
  InformationCircleIcon,
  BoltIcon,
  CpuChipIcon,
  LockClosedIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline'
import { Button } from '../../components/ui'

const InfoTip: React.FC<{
  content: React.ReactNode
  label?: string
  variant?: 'default' | 'onColor'
  position?: 'top' | 'bottom' | 'left' | 'right'
}> = ({ content, label = 'Saiba mais', variant = 'default', position = 'bottom' }) => (
  <Tooltip content={content} position={position} constrainWidth className="inline-flex shrink-0 align-middle">
    <button
      type="button"
      className={
        variant === 'onColor'
          ? 'inline-flex rounded-full p-0.5 text-white/85 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/50'
          : 'inline-flex rounded-full p-0.5 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900'
      }
      aria-label={label}
    >
      <InformationCircleIcon className="h-4 w-4" />
    </button>
  </Tooltip>
)

const HELP_PAGINA = (
  <div className="space-y-1.5 text-left">
    <p className="font-semibold">Sobre esta página</p>
    <p>
      Consolida cadastro de usuários, execuções agendadas, disponibilidade da API, uso de disco do Postgres, desempenho
      (estatísticas), tamanho dos arquivos no Storage e processos PM2/Nginx no servidor.
    </p>
  </div>
)

const HELP_INDICADORES_TOPO = (
  <div className="space-y-1.5 text-left">
    <p className="font-semibold">Indicadores do topo</p>
    <p>Usuários inativos/total: contagem de perfis com is_active = false frente ao total de perfis.</p>
    <p>Execuções com erro: falhas nas últimas 100 linhas de process_run_logs; não representa o histórico completo.</p>
    <p>Health da API: verificação a partir do navegador; bloqueio de CORS ou falha de rede não prova que a API esteja fora do ar.</p>
  </div>
)

const HELP_USUARIOS_INATIVOS = (
  <p className="text-left">Inativos = usuários com perfil is_active = false no cadastro.</p>
)

const HELP_EXECUCOES_ERRO = (
  <p className="text-left">Contagem a partir de process_run_logs (últimas 100 execuções); não é o total histórico de falhas.</p>
)

const HELP_API_HEALTH = (
  <p className="text-left">Teste de health no navegador; restrição de CORS ou rede pode impedir a leitura mesmo com a API operacional.</p>
)

const HELP_SUPABASE_SECAO = (
  <div className="space-y-1.5 text-left">
    <p className="font-semibold">Como comparar os números</p>
    <p>
      O card azul soma o tamanho em disco de todos os bancos da instância Postgres (útil para a cota de 8 GB). O valor
      &quot;banco atual&quot; é só o banco da conexão — em geral um pouco menor.
    </p>
    <p>
      Storage (buckets) soma tamanhos declarados dos arquivos; isso não entra no pg_database_size da mesma forma. Analise
      Postgres e Storage separadamente.
    </p>
  </div>
)

const HELP_POSTGRES_DISCO_TOTAL = (
  <p className="text-left">
    Valor da RPC que soma pg_database_size de cada banco da instância (ex.: postgres, templates). Não inclui o volume
    dos arquivos do Storage.
  </p>
)

const HELP_BANCO_CONEXAO = (
  <p className="text-left">
    Tamanho em disco apenas do banco da sessão (current_database). O card azul acima soma todos os bancos da instância,
    por isso pode ser maior.
  </p>
)

const HELP_TABELAS_TAMANHO = (
  <p className="text-left">
    Cada linha é uma tabela (relação): tamanho inclui heap, índices e TOAST. Ordenação do maior para o menor ajuda a
    achar o que mais ocupa espaço no banco da conexão.
  </p>
)

const HELP_BUCKETS = (
  <p className="text-left">
    Soma dos tamanhos declarados dos objetos no Storage (metadados). Os arquivos não entram no que o Postgres reporta
    em pg_database_size; use este bloco para mídia e anexos.
  </p>
)

const HELP_SERVIDOR_SECAO = (
  <p className="text-left">
    Dados da API de monitoramento: PM2 (processos Node) e Nginx (sites/configs) na mesma tabela. A coluna Tipo indica a
    origem de cada linha. Ativos/total indicam quantos estão online no inventário; expanda a tabela para uptime,
    memória e portas.
  </p>
)

const HELP_PM2_RESUMO = (
  <p className="text-left">Online = processos com status online; total = processos cadastrados no inventário do servidor.</p>
)

const HELP_NGINX_RESUMO = (
  <p className="text-left">Ativos = projetos com status active; total = configs detectadas no inventário.</p>
)

const WEBHOOK_SERVIDOR_DIAGNOSTICO =
  'https://webhook.pasiai.com/webhook/93335b10-84d4-4101-8b03-b19fe2e400e2'

type WebhookOutputItem = {
  output?: string
  template_id?: number
  responses?: Array<{
    status?: string
    output?: string
  }>
}

function extractWebhookOutput(payload: unknown): string | null {
  if (!Array.isArray(payload)) return null
  for (const item of payload) {
    const entry = item as WebhookOutputItem
    if (typeof entry?.output === 'string' && entry.output.trim()) {
      return entry.output
    }
    const responses = entry?.responses
    if (!Array.isArray(responses)) continue
    for (const r of responses) {
      if (typeof r?.output === 'string' && r.output.trim()) {
        return r.output
      }
    }
  }
  return null
}

type PerformanceSummaryJson = {
  numbackends?: number
  max_connections?: number
  xact_commit?: number
  xact_rollback?: number
  blks_read?: number
  blks_hit?: number
  cache_hit_ratio_pct?: number | null
  deadlocks?: number
  temp_files?: number
  temp_bytes?: number
  database?: string
}

type MonitoringDashboardJson = {
  performance_summary?: unknown
  performance_interval?: unknown
  maintenance_health?: unknown
  connection_risks?: unknown
  activity_summary?: unknown
  seq_scan_candidates?: unknown
  top_pg_stat_statements?: unknown
  lock_blocking?: unknown
  connections?: unknown
}

type PerformanceIntervalJson = {
  window_minutes?: number
  has_enough_points?: boolean
  message?: string | null
  from?: string | null
  to?: string | null
  seconds_between?: number | null
  tps?: number | null
  rollbacks_per_min?: number | null
  deadlocks_per_min?: number | null
  temp_mb_per_min?: number | null
}

type ConnectionRiskRow = {
  pid: number
  usename: string
  application_name: string
  state: string
  xact_age: string
  state_age: string
  wait_event_type: string
  wait_event: string
  query_preview: string
}

type ConnectionRisksJson = {
  idle_in_transaction_threshold_min?: number
  active_threshold_min?: number
  idle_in_transaction_over_threshold?: number
  active_over_threshold?: number
  top_by_xact_age?: unknown
}

/** PostgREST pode devolver JSONB como objeto ou, em alguns casos, string JSON. Aceita também maxConnections. */
function parsePerformanceSummaryRpc(data: unknown): PerformanceSummaryJson | null {
  if (data == null) return null
  let obj: unknown = data
  if (typeof data === 'string') {
    try {
      obj = JSON.parse(data) as unknown
    } catch {
      return null
    }
  }
  if (obj == null || typeof obj !== 'object') return null
  return obj as PerformanceSummaryJson
}

function parseMonitoringDashboardRpc(data: unknown): MonitoringDashboardJson | null {
  if (data == null) return null
  let obj: unknown = data
  if (typeof data === 'string') {
    try {
      obj = JSON.parse(data) as unknown
    } catch {
      return null
    }
  }
  if (obj == null || typeof obj !== 'object') return null
  return obj as MonitoringDashboardJson
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

function parsePerformanceIntervalRpc(data: unknown): PerformanceIntervalJson | null {
  if (data == null) return null
  let obj: unknown = data
  if (typeof data === 'string') {
    try {
      obj = JSON.parse(data) as unknown
    } catch {
      return null
    }
  }
  if (obj == null || typeof obj !== 'object') return null
  return obj as PerformanceIntervalJson
}

function parseConnectionRisksRpc(data: unknown): ConnectionRisksJson | null {
  if (data == null) return null
  let obj: unknown = data
  if (typeof data === 'string') {
    try {
      obj = JSON.parse(data) as unknown
    } catch {
      return null
    }
  }
  if (obj == null || typeof obj !== 'object') return null
  return obj as ConnectionRisksJson
}

function readMaxConnections(perf: PerformanceSummaryJson | null): number | null {
  if (!perf) return null
  const r = perf as Record<string, unknown>
  const v = r.max_connections ?? r.maxConnections
  if (v == null || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

const HELP_DESEMPENHO_SECAO = (
  <div className="space-y-1.5 text-left">
    <p className="font-semibold">Desempenho do Postgres</p>
    <p>
      Métricas vindas de pg_stat_database e pg_stat_activity do banco atual. São valores cumulativos desde o último
      reset das estatísticas — indicadores de tendência, não diagnóstico isolado.
    </p>
  </div>
)

const HELP_CACHE_HIT = (
  <p className="text-left">
    Proporção de leituras atendidas pelo buffer cache (blks_hit / leituras totais). Valores muito abaixo de ~99% em
    carga estável podem sugerir pressão de memória ou muitas leituras &quot;frias&quot;.
  </p>
)

const HELP_CONEXOES = (
  <p className="text-left">
    Formato atual/limite: backends conectados ao banco atual (pg_stat_database.numbackends) e máximo permitido pela
    instância (max_connections em pg_settings). O plano Supabase pode impor limites adicionais no pooler.
  </p>
)

const HELP_TRANSACOES = (
  <p className="text-left">
    Commits e rollbacks acumulados. Muitos rollbacks em relação aos commits podem indicar erros de aplicação ou
    cancelamentos frequentes.
  </p>
)

const HELP_DEADLOCKS = (
  <p className="text-left">Deadlocks detectados desde o último reset. Qualquer valor acima de zero merece análise nos logs e ordem de acesso às tabelas.</p>
)

const HELP_DEADLOCKS_SECAO = (
  <div className="space-y-1.5 text-left">
    <p>
      O Postgres não guarda, nas views do sistema, a lista de cada deadlock ocorrido — apenas o contador cumulativo (card
      acima). O detalhe de cada evento (PIDs, consultas) fica nos logs do servidor; no Supabase: Logs → Postgres, procure
      por &quot;deadlock detected&quot;.
    </p>
    <p>
      A tabela abaixo mostra, neste instante, sessões do banco atual à espera de lock e a sessão que detém o lock — útil
      para ver contenção antes ou depois de deadlocks.
    </p>
  </div>
)

const HELP_TEMP_IO = (
  <p className="text-left">
    Arquivos temporários e bytes escritos para ordenações/agrupamentos grandes (work_mem). Picos podem indicar consultas
    pesadas ou work_mem insuficiente para o padrão de carga.
  </p>
)

const HELP_SESSOES_ESTADO = (
  <p className="text-left">
    Distribuição de sessões por state no banco atual. Muitos idle in transaction ou active prolongado podem sinalizar
    bloqueios ou conexões mal fechadas.
  </p>
)

const HELP_CONEXOES_DETALHE = (
  <div className="space-y-1.5 text-left">
    <p>
      Lista sessões atuais do banco (`pg_stat_activity`) com tempo de conexão (`connected_for`), tempo no estado atual
      (`state_for`) e idade da transação (`xact_age`).
    </p>
    <p>
      A coluna origem classifica conexões como internas do Supabase, editor SQL do dashboard ou provável app, com base em
      usuário, aplicação, tipo de backend e IP.
    </p>
  </div>
)

const HELP_SEQ_SCAN = (
  <p className="text-left">
    Tabelas com mais sequential scans entre as que têm pelo menos 10 scans no total. Alta proporção de seq_scan pode
    indicar índices faltando — confirme com planos de consulta antes de criar índices.
  </p>
)

const HELP_PG_STAT_STMT = (
  <p className="text-left">
    Consultas com maior tempo total de execução registrado em pg_stat_statements (texto truncado). Requer extensão
    pg_stat_statements habilitada no Supabase. Se a lista estiver vazia, habilite a extensão ou aguarde tráfego.
  </p>
)

const HELP_TENDENCIA_INTERVALO = (
  <p className="text-left">
    Indicadores por intervalo usam delta entre snapshots recentes. Se o sistema ainda não coletou pontos suficientes,
    os valores ficam indisponíveis temporariamente.
  </p>
)

const HELP_MANUTENCAO = (
  <p className="text-left">
    Tabelas com dead tuples elevados e manutenção antiga tendem a degradar leitura/escrita. Use como triagem para
    investigar vacuum/analyze.
  </p>
)

const HELP_CONEXOES_RISCO = (
  <p className="text-left">
    Resume sessões possivelmente problemáticas: idle in transaction acima do limiar e active prolongado, além das
    transações mais antigas no momento.
  </p>
)

const ConfiguracoesMonitoramento: React.FC = () => {
  const [moduleTitle, setModuleTitle] = useState<string>('Monitoramento')
  const [moduleDescription, setModuleDescription] = useState<string>('Acompanhe indicadores do sistema.')
  
  // Estados para indicadores
  const [inactiveUsersCount, setInactiveUsersCount] = useState<number | null>(null)
  const [totalUsersCount, setTotalUsersCount] = useState<number | null>(null)
  const [failedExecutionsCount, setFailedExecutionsCount] = useState<number | null>(null)
  const [serverStatus, setServerStatus] = useState<'ok' | 'error' | 'checking' | 'cors' | null>(null)
  const [indicatorsLoading, setIndicatorsLoading] = useState<boolean>(false)
  const [indicatorsError, setIndicatorsError] = useState<string | null>(null)

  // Estados para indicadores do Supabase
  const [databaseSize, setDatabaseSize] = useState<number | null>(null)
  const [bucketsSize, setBucketsSize] = useState<Array<{ bucket_id: string; total_size_mb: number }>>([])
  const [databaseSizePretty, setDatabaseSizePretty] = useState<string | null>(null)
  const [userTablesSizeRows, setUserTablesSizeRows] = useState<Array<{ tabela: string; tamanho: string }>>([])
  const [supabaseIndicatorsLoading, setSupabaseIndicatorsLoading] = useState<boolean>(false)
  const [supabaseIndicatorsError, setSupabaseIndicatorsError] = useState<string | null>(null)
  const [bucketsExpanded, setBucketsExpanded] = useState<boolean>(false)
  const [dbPrettyExpanded, setDbPrettyExpanded] = useState<boolean>(false)
  const [userTablesExpanded, setUserTablesExpanded] = useState<boolean>(false)

  const [performanceSummary, setPerformanceSummary] = useState<PerformanceSummaryJson | null>(null)
  const [activitySummaryRows, setActivitySummaryRows] = useState<Array<{ estado: string; quantidade: number }>>([])
  const [seqScanRows, setSeqScanRows] = useState<
    Array<{ relname: string; seq_scan: number; idx_scan: number; seq_scan_pct: number | null }>
  >([])
  const [stmtRows, setStmtRows] = useState<
    Array<{ query_preview: string; calls: number; total_exec_time_ms: number; mean_exec_time_ms: number }>
  >([])
  const [performanceLoading, setPerformanceLoading] = useState<boolean>(false)
  const [performanceError, setPerformanceError] = useState<string | null>(null)
  const [activitySummaryError, setActivitySummaryError] = useState<string | null>(null)
  const [perfActivityExpanded, setPerfActivityExpanded] = useState<boolean>(false)
  const [perfSeqExpanded, setPerfSeqExpanded] = useState<boolean>(false)
  const [perfStmtExpanded, setPerfStmtExpanded] = useState<boolean>(false)
  const [connectionRows, setConnectionRows] = useState<
    Array<{
      pid: number
      usename: string
      application_name: string
      client_addr: string
      backend_type: string
      state: string
      connected_for: string
      state_for: string
      xact_age: string
      wait_event_type: string
      wait_event: string
      query_preview: string
      origem: string
    }>
  >([])
  const [connectionsError, setConnectionsError] = useState<string | null>(null)
  const [perfConnectionsExpanded, setPerfConnectionsExpanded] = useState<boolean>(false)
  const [lockBlockingRows, setLockBlockingRows] = useState<
    Array<{
      bloqueado_pid: number
      bloqueando_pid: number
      locktype: string
      modo: string
      relacao: string | null
      bloqueado_estado: string | null
      bloqueando_estado: string | null
      bloqueado_query: string | null
      bloqueando_query: string | null
    }>
  >([])
  const [lockBlockingError, setLockBlockingError] = useState<string | null>(null)
  const [perfLocksExpanded, setPerfLocksExpanded] = useState<boolean>(false)
  const [performanceInterval, setPerformanceInterval] = useState<PerformanceIntervalJson | null>(null)
  const [maintenanceRows, setMaintenanceRows] = useState<
    Array<{
      schemaname: string
      relname: string
      n_live_tup: number
      n_dead_tup: number
      dead_tuple_pct: number | null
      last_autovacuum: string | null
      last_autoanalyze: string | null
      autovacuum_age_minutes: number | null
      autoanalyze_age_minutes: number | null
      maintenance_risk_score: number | null
    }>
  >([])
  const [connectionRisks, setConnectionRisks] = useState<ConnectionRisksJson | null>(null)
  const [connectionRiskTopRows, setConnectionRiskTopRows] = useState<ConnectionRiskRow[]>([])
  const [perfIntervalExpanded, setPerfIntervalExpanded] = useState<boolean>(false)
  const [maintenanceExpanded, setMaintenanceExpanded] = useState<boolean>(false)
  const [connectionRisksExpanded, setConnectionRisksExpanded] = useState<boolean>(false)

  // Estados para informações do servidor 10.60.60.115
  const [serverInfo, setServerInfo] = useState<{
    pm2: { processes: PM2Process[]; total: number }
    nginx: { projects: NginxProject[]; total: number }
  } | null>(null)
  const [serverInfoLoading, setServerInfoLoading] = useState<boolean>(false)
  const [serverInfoError, setServerInfoError] = useState<string | null>(null)
  const [processesExpanded, setProcessesExpanded] = useState<boolean>(false)
  const [serverDiagLoading, setServerDiagLoading] = useState<boolean>(false)
  const [serverDiagExecuted, setServerDiagExecuted] = useState<boolean>(false)
  const [serverDiagError, setServerDiagError] = useState<string | null>(null)
  const [serverDiagOutput, setServerDiagOutput] = useState<string | null>(null)

  const [copiedSupabase, setCopiedSupabase] = useState(false)
  const [copiedDesempenho, setCopiedDesempenho] = useState(false)
  const [snapshotSaving, setSnapshotSaving] = useState<boolean>(false)
  const [snapshotMessage, setSnapshotMessage] = useState<string | null>(null)

  const copyJsonToClipboard = async (data: unknown, setCopied: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // fallback
    }
  }

  const downloadJsonFile = (data: unknown, fileNameBase: string) => {
    const json = JSON.stringify(data, null, 2)
    const blob = new Blob([json], { type: 'application/json;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    try {
      const now = new Date()
      const pad = (n: number) => String(n).padStart(2, '0')
      const dateTime = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`
      const a = document.createElement('a')
      a.href = url
      a.download = `${fileNameBase}-${dateTime}.json`
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      a.remove()
    } finally {
      URL.revokeObjectURL(url)
    }
  }

  const buildSupabaseJson = () => ({
    secao: 'Indicadores do Supabase',
    timestamp: new Date().toISOString(),
    tamanho_banco_total_mb: databaseSize,
    capacidade_total_mb: 8192,
    tamanho_banco_atual: databaseSizePretty,
    tabelas: userTablesSizeRows,
    buckets: bucketsSize,
  })

  const buildDesempenhoJson = () => ({
    secao: 'Desempenho do Postgres',
    timestamp: new Date().toISOString(),
    resumo: performanceSummary,
    sessoes_por_estado: activitySummaryRows,
    conexoes: connectionRows,
    tendencias_intervalo: performanceInterval,
    saude_manutencao: maintenanceRows,
    riscos_conexao: connectionRisks,
    top_transacoes_antigas: connectionRiskTopRows,
    bloqueios_lock: lockBlockingRows,
    candidatas_seq_scan: seqScanRows,
    consultas_pesadas_pg_stat_statements: stmtRows,
  })

  // Buscar título e descrição do módulo
  useEffect(() => {
    const loadModuleInfo = async () => {
      try {
        const result = await modulesService.getByName('configuracoes_monitoramento')
        if (result.success && result.data) {
          if (result.data.display_name) {
            setModuleTitle(result.data.display_name)
          }
          if (result.data.description) {
            setModuleDescription(result.data.description)
          }
        }
      } catch (error) {
        console.error('Erro ao buscar informações do módulo:', error)
      }
    }

    loadModuleInfo()
  }, [])

  // Função para carregar indicadores (extraída para poder ser chamada manualmente)
  const loadIndicators = async () => {
      try {
        setIndicatorsLoading(true)
        setIndicatorsError(null)
        setServerStatus('checking')

        // Buscar status do servidor e dados do Supabase em paralelo
        const [serverStatusResult, { count: inactiveCount, error: inactiveError }, { count: totalCount, error: totalError }, last100LogsResult] = await Promise.all([
          (async (): Promise<'ok' | 'error' | 'cors'> => {
            try {
              // Criar um AbortController para timeout
              const controller = new AbortController()
              const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 segundos de timeout
              
              const response = await fetch('https://iaapi.portalpasi.com.br/health', {
                method: 'GET',
                headers: {
                  'Accept': 'application/json',
                },
                signal: controller.signal,
              })
              
              clearTimeout(timeoutId)
              
              if (response.ok) {
                const data = await response.json()
                // Verificar se o status é 'ok' (case-insensitive para maior robustez)
                if (data && data.status && String(data.status).toLowerCase() === 'ok') {
                  return 'ok'
                }
                return 'error'
              }
              return 'error'
            } catch (error: any) {
              if (error.name === 'AbortError') {
                return 'error'
              }
              // Verificar se é erro de CORS
              if (error.message && error.message.includes('CORS') || error.message === 'Failed to fetch') {
                return 'cors'
              }
              return 'error'
            }
          })(),
          supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('is_active', false),
          supabase.from('profiles').select('*', { count: 'exact', head: true }),
          supabase.from('process_run_logs').select('status').order('started_at', { ascending: false }).limit(100)
        ])

        if (inactiveError) throw inactiveError
        if (totalError) throw totalError
        if (last100LogsResult.error) throw last100LogsResult.error

        // Contar erros nas últimas 100 execuções
        const failedCount = (last100LogsResult.data || []).filter((log: any) => log.status === 'failed').length

        setInactiveUsersCount(inactiveCount ?? 0)
        setTotalUsersCount(totalCount ?? 0)
        setFailedExecutionsCount(failedCount)
        setServerStatus(serverStatusResult)
      } catch (e: any) {
        setIndicatorsError(e?.message || 'Erro ao carregar indicadores')
        setInactiveUsersCount(null)
        setTotalUsersCount(null)
        setFailedExecutionsCount(null)
        // Tentar buscar status do servidor mesmo em caso de erro nas outras consultas
        try {
          const controller = new AbortController()
          const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 segundos de timeout
          
          const response = await fetch('https://iaapi.portalpasi.com.br/health', {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
            },
            signal: controller.signal,
          })
          
          clearTimeout(timeoutId)
          
          if (response.ok) {
            const data = await response.json()
            if (data && data.status && String(data.status).toLowerCase() === 'ok') {
              setServerStatus('ok')
            } else {
              setServerStatus('error')
            }
          } else {
            setServerStatus('error')
          }
        } catch (error: any) {
          if (error.name === 'AbortError') {
            setServerStatus('error')
          } else if (error.message && (error.message.includes('CORS') || error.message === 'Failed to fetch')) {
            setServerStatus('cors')
          } else {
            setServerStatus('error')
          }
        }
      } finally {
        setIndicatorsLoading(false)
      }
    }

  // Carregar indicadores ao montar o componente
  useEffect(() => {
    loadIndicators()
  }, [])

  // Função para carregar indicadores do Supabase (extraída para poder ser chamada manualmente)
  const loadSupabaseIndicators = async () => {
      try {
        setSupabaseIndicatorsLoading(true)
        setSupabaseIndicatorsError(null)

        // Buscar tamanho do banco, buckets e relações de catálogo (via RPC) em paralelo
        const [dbSizeResult, bucketsResult, dbPrettyResult, tablesResult] = await Promise.all([
          supabase.rpc('get_database_size_mb'),
          supabase.rpc('get_buckets_size_mb'),
          supabase.rpc('get_current_database_size_pretty'),
          supabase.rpc('list_user_tables_size_pretty')
        ])

        if (dbSizeResult.error) throw dbSizeResult.error
        if (bucketsResult.error) throw bucketsResult.error
        if (dbPrettyResult.error) throw dbPrettyResult.error
        if (tablesResult.error) throw tablesResult.error

        setDatabaseSize(dbSizeResult.data as number)
        setBucketsSize((bucketsResult.data || []) as Array<{ bucket_id: string; total_size_mb: number }>)
        setDatabaseSizePretty(
          typeof dbPrettyResult.data === 'string' ? dbPrettyResult.data : null
        )
        setUserTablesSizeRows(
          (tablesResult.data || []) as Array<{ tabela: string; tamanho: string }>
        )
      } catch (e: any) {
        setSupabaseIndicatorsError(e?.message || 'Erro ao carregar indicadores do Supabase')
        setDatabaseSize(null)
        setBucketsSize([])
        setDatabaseSizePretty(null)
        setUserTablesSizeRows([])
      } finally {
        setSupabaseIndicatorsLoading(false)
      }
    }

  // Carregar indicadores do Supabase ao montar o componente
  useEffect(() => {
    loadSupabaseIndicators()
  }, [])

  const loadDatabasePerformance = async () => {
    setPerformanceLoading(true)
    setPerformanceError(null)
    setActivitySummaryError(null)
    setLockBlockingError(null)
    setConnectionsError(null)

    const dashboardR = await supabase.rpc('get_monitoring_dashboard', {
      p_seq_limit: 25,
      p_stmt_limit: 15,
      p_conn_limit: 200,
      p_interval_window_minutes: 15,
      p_idle_threshold_minutes: 5,
      p_active_threshold_minutes: 15,
      p_maintenance_limit: 20,
    })

    if (dashboardR.error) {
      const msg = dashboardR.error.message || 'Erro ao carregar monitoramento consolidado'
      setPerformanceError(msg)
      setActivitySummaryError(msg)
      setLockBlockingError(msg)
      setConnectionsError(msg)
      setPerformanceSummary(null)
      setActivitySummaryRows([])
      setSeqScanRows([])
      setStmtRows([])
      setLockBlockingRows([])
      setConnectionRows([])
      setPerformanceInterval(null)
      setMaintenanceRows([])
      setConnectionRisks(null)
      setConnectionRiskTopRows([])
      setPerformanceLoading(false)
      return
    }

    const payload = parseMonitoringDashboardRpc(dashboardR.data)
    if (!payload) {
      const msg = 'Payload inválido da RPC get_monitoring_dashboard'
      setPerformanceError(msg)
      setActivitySummaryError(msg)
      setLockBlockingError(msg)
      setConnectionsError(msg)
      setPerformanceSummary(null)
      setActivitySummaryRows([])
      setSeqScanRows([])
      setStmtRows([])
      setLockBlockingRows([])
      setConnectionRows([])
      setPerformanceInterval(null)
      setMaintenanceRows([])
      setConnectionRisks(null)
      setConnectionRiskTopRows([])
      setPerformanceLoading(false)
      return
    }

    setPerformanceSummary(parsePerformanceSummaryRpc(payload.performance_summary))
    setActivitySummaryRows(asArray<{ estado: string; quantidade: number }>(payload.activity_summary))
    setSeqScanRows(
      asArray<{
        relname: string
        seq_scan: number
        idx_scan: number
        seq_scan_pct: number | null
      }>(payload.seq_scan_candidates)
    )
    setStmtRows(
      asArray<{
        query_preview: string
        calls: number
        total_exec_time_ms: number
        mean_exec_time_ms: number
      }>(payload.top_pg_stat_statements)
    )
    setLockBlockingRows(
      asArray<{
        bloqueado_pid: number
        bloqueando_pid: number
        locktype: string
        modo: string
        relacao: string | null
        bloqueado_estado: string | null
        bloqueando_estado: string | null
        bloqueado_query: string | null
        bloqueando_query: string | null
      }>(payload.lock_blocking)
    )
    setConnectionRows(
      asArray<{
        pid: number
        usename: string
        application_name: string
        client_addr: string
        backend_type: string
        state: string
        connected_for: string
        state_for: string
        xact_age: string
        wait_event_type: string
        wait_event: string
        query_preview: string
        origem: string
      }>(payload.connections)
    )
    const risks = parseConnectionRisksRpc(payload.connection_risks)
    setPerformanceInterval(parsePerformanceIntervalRpc(payload.performance_interval))
    setMaintenanceRows(
      asArray<{
        schemaname: string
        relname: string
        n_live_tup: number
        n_dead_tup: number
        dead_tuple_pct: number | null
        last_autovacuum: string | null
        last_autoanalyze: string | null
        autovacuum_age_minutes: number | null
        autoanalyze_age_minutes: number | null
        maintenance_risk_score: number | null
      }>(payload.maintenance_health)
    )
    setConnectionRisks(risks)
    setConnectionRiskTopRows(asArray<ConnectionRiskRow>(risks?.top_by_xact_age))

    setPerformanceLoading(false)
  }

  useEffect(() => {
    loadDatabasePerformance()
  }, [])

  // Função para carregar informações do servidor 10.60.60.115
  const loadServerInfo = async () => {
    try {
      setServerInfoLoading(true)
      setServerInfoError(null)

      const result = await serverInfoService.getServerInfoWithAuth()

      if (result.success && result.data) {
        setServerInfo(result.data)
      } else {
        throw new Error(result.error || 'Erro ao carregar informações do servidor')
      }
    } catch (e: any) {
      setServerInfoError(e?.message || 'Erro ao carregar informações do servidor')
      setServerInfo(null)
    } finally {
      setServerInfoLoading(false)
    }
  }

  // Carregar informações do servidor ao montar o componente
  useEffect(() => {
    loadServerInfo()
  }, [])

  const handleServerDiagnosticWebhook = async () => {
    setServerDiagExecuted(true)
    try {
      setServerDiagLoading(true)
      setServerDiagError(null)
      setServerDiagOutput(null)

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 120000)

      const response = await fetch(WEBHOOK_SERVIDOR_DIAGNOSTICO, {
        method: 'GET',
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        let detail = `Erro HTTP ${response.status}`
        try {
          const errJson = await response.json()
          if (errJson?.message) detail = String(errJson.message)
        } catch {
          const errText = await response.text().catch(() => '')
          if (errText) detail = errText
        }
        throw new Error(detail)
      }

      const data: unknown = await response.json()
      const output = extractWebhookOutput(data)
      if (!output) {
        throw new Error('Resposta recebida, mas sem campo output reconhecível.')
      }
      setServerDiagOutput(output)
    } catch (e: unknown) {
      const msg =
        e instanceof Error
          ? e.name === 'AbortError'
            ? 'Tempo esgotado (120s). O diagnóstico pode demorar; tente novamente.'
            : e.message
          : 'Erro ao acionar o webhook.'
      setServerDiagError(msg)
    } finally {
      setServerDiagLoading(false)
    }
  }

  // Função para atualizar todas as informações
  const handleRefresh = async () => {
    await Promise.all([
      loadIndicators(),
      loadSupabaseIndicators(),
      loadDatabasePerformance(),
      loadServerInfo()
    ])
  }

  const handleCaptureSnapshot = async () => {
    try {
      setSnapshotSaving(true)
      setSnapshotMessage(null)
      const { error } = await supabase.rpc('capture_monitoring_stats_snapshot')
      if (error) throw error
      setSnapshotMessage('Snapshot gravado com sucesso.')
      await loadDatabasePerformance()
    } catch (e: any) {
      setSnapshotMessage(e?.message || 'Erro ao gravar snapshot.')
    } finally {
      setSnapshotSaving(false)
      setTimeout(() => setSnapshotMessage(null), 3500)
    }
  }

  // Função para formatar tamanho em MB para GB se necessário
  const formatSize = (sizeMb: number | null): string => {
    if (sizeMb === null) return '—'
    if (sizeMb >= 1024) {
      return `${(sizeMb / 1024).toFixed(2)} GB`
    }
    return `${sizeMb.toFixed(2)} MB`
  }

  // Capacidade total do banco de dados em MB (8GB = 8192 MB)
  const DATABASE_CAPACITY_MB = 8192

  // Calcular porcentagem de uso do banco de dados
  const getDatabaseUsagePercentage = (): number => {
    if (databaseSize === null) return 0
    return Math.min((databaseSize / DATABASE_CAPACITY_MB) * 100, 100)
  }

  // Obter cor da barra de progresso baseado na porcentagem
  const getProgressBarColor = (percentage: number): string => {
    if (percentage >= 90) return 'bg-red-500'
    if (percentage >= 75) return 'bg-orange-500'
    if (percentage >= 50) return 'bg-yellow-500'
    return 'bg-green-500'
  }

  const getCacheHitCardGradient = (pct: number | null | undefined): string => {
    if (pct == null) return 'from-gray-500 to-gray-600'
    if (pct >= 99) return 'from-green-500 to-green-600'
    if (pct >= 95) return 'from-yellow-500 to-yellow-600'
    return 'from-orange-500 to-red-600'
  }

  // Função para formatar uptime (milissegundos para formato legível)
  const formatUptime = (ms: number): string => {
    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 0) {
      return `${days}d ${hours % 24}h ${minutes % 60}m`
    }
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`
    }
    if (minutes > 0) {
      return `${minutes}m`
    }
    return `${seconds}s`
  }

  // Função para formatar memória (bytes para MB/GB)
  const formatMemory = (bytes: number): string => {
    const mb = bytes / (1024 * 1024)
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`
    }
    return `${mb.toFixed(2)} MB`
  }

  // Função para obter cor do status
  const getStatusColor = (status: string): string => {
    const statusLower = status.toLowerCase()
    if (statusLower === 'online' || statusLower === 'active') {
      return 'text-green-600 dark:text-green-400'
    }
    return 'text-red-600 dark:text-red-400'
  }

  // Função para obter badge de status
  const getStatusBadgeColor = (status: string): string => {
    const statusLower = status.toLowerCase()
    if (statusLower === 'online' || statusLower === 'active') {
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
    }
    return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
  }

  return (
    <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard 
          moduleName="configuracoes_monitoramento" 
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">Acesso Negado</Text>
                <Text size="sm" color="muted">Você não tem permissão para acessar esta página.</Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-start justify-between">
                <div className="min-w-0 pr-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                      {moduleTitle}
                    </Heading>
                    <InfoTip content={HELP_PAGINA} label="Sobre esta página" position="bottom" />
                  </div>
                  <Text size="sm" color="muted" className="mt-2">
                    {moduleDescription}
                  </Text>
                </div>
                <div className="!fixed !bottom-6 !right-6 !z-50 flex items-center gap-2">
                  <Button
                    onClick={handleCaptureSnapshot}
                    disabled={snapshotSaving}
                    variant="outline"
                    className="flex items-center gap-2 rounded-full bg-white shadow-lg dark:bg-gray-800"
                  >
                    <ServerIcon className={`h-5 w-5 ${snapshotSaving ? 'animate-pulse' : ''}`} />
                    Gravar snapshot
                  </Button>
                  <Button
                    onClick={handleRefresh}
                    disabled={indicatorsLoading || supabaseIndicatorsLoading || performanceLoading || serverInfoLoading}
                    variant="outline"
                    className="flex items-center gap-2 rounded-full bg-white shadow-lg dark:bg-gray-800"
                  >
                    <ArrowPathIcon className={`h-5 w-5 ${(indicatorsLoading || supabaseIndicatorsLoading || performanceLoading || serverInfoLoading) ? 'animate-spin' : ''}`} />
                    Atualizar
                  </Button>
                </div>
              </div>
              {snapshotMessage && (
                <div className="mt-3">
                  <Text size="sm" color="muted">{snapshotMessage}</Text>
                </div>
              )}
            </div>

            {/* Indicadores */}
            <div className="px-6 py-3">
              <div className="flex justify-end mb-2">
                <InfoTip content={HELP_INDICADORES_TOPO} label="Ajuda — indicadores do topo" position="bottom" />
              </div>
              {indicatorsLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3">
                    <div className="text-center">
                      <div className="animate-pulse">
                        <div className="h-4 w-16 bg-gray-300 dark:bg-gray-600 rounded mx-auto mb-1"></div>
                        <div className="h-2 w-32 bg-gray-300 dark:bg-gray-600 rounded mx-auto"></div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3">
                    <div className="text-center">
                      <div className="animate-pulse">
                        <div className="h-4 w-16 bg-gray-300 dark:bg-gray-600 rounded mx-auto mb-1"></div>
                        <div className="h-2 w-32 bg-gray-300 dark:bg-gray-600 rounded mx-auto"></div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3">
                    <div className="text-center">
                      <div className="animate-pulse">
                        <div className="h-4 w-16 bg-gray-300 dark:bg-gray-600 rounded mx-auto mb-1"></div>
                        <div className="h-2 w-32 bg-gray-300 dark:bg-gray-600 rounded mx-auto"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {indicatorsError && (
                    <div className="mb-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                      <Text size="sm" className="text-yellow-800 dark:text-yellow-200">
                        {indicatorsError}
                      </Text>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className={`bg-gradient-to-br shadow-lg rounded-lg p-3 text-white ${
                      inactiveUsersCount !== null && inactiveUsersCount <= 0
                        ? 'from-green-500 to-green-600'
                        : 'from-red-500 to-red-600'
                    }`}>
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1">
                          <UserIcon className="h-5 w-5 mr-2 text-white" />
                        </div>
                        <Text size="2xl" className="text-white font-bold mb-1">
                          {inactiveUsersCount ?? '—'}/{totalUsersCount ?? '—'}
                        </Text>
                        <div className="flex items-center justify-center gap-1">
                          <Link 
                            to="/configuracoes/usuarios" 
                            className="text-white hover:opacity-90 underline"
                          >
                            <Text size="xs" className="text-white">
                              Usuários inativos / total
                            </Text>
                          </Link>
                          <InfoTip content={HELP_USUARIOS_INATIVOS} label="O que são usuários inativos" variant="onColor" position="bottom" />
                        </div>
                      </div>
                    </div>
                    <div className={`bg-gradient-to-br shadow-lg rounded-lg p-3 text-white ${
                      failedExecutionsCount !== null && failedExecutionsCount <= 0
                        ? 'from-green-500 to-green-600'
                        : 'from-red-500 to-red-600'
                    }`}>
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1">
                          <ExclamationTriangleIcon className="h-5 w-5 mr-2 text-white" />
                        </div>
                        <Text size="2xl" className="text-white font-bold mb-1">
                          {failedExecutionsCount ?? '—'}
                        </Text>
                        <div className="flex items-center justify-center gap-1">
                          <Link 
                            to="/configuracoes/agendamentos" 
                            className="text-white hover:opacity-90 underline"
                          >
                            <Text size="xs" className="text-white">
                              Execuções com erro (últimas 100)
                            </Text>
                          </Link>
                          <InfoTip content={HELP_EXECUCOES_ERRO} label="Como é contada a execução com erro" variant="onColor" position="bottom" />
                        </div>
                      </div>
                    </div>
                    <div className={`bg-gradient-to-br shadow-lg rounded-lg p-3 text-white ${
                      serverStatus === 'ok' 
                        ? 'from-green-500 to-green-600' 
                        : serverStatus === 'checking'
                        ? 'from-yellow-500 to-yellow-600'
                        : serverStatus === 'cors'
                        ? 'from-primary-500 to-primary-600'
                        : serverStatus === null
                        ? 'from-gray-500 to-gray-600'
                        : 'from-red-500 to-red-600'
                    }`}>
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1">
                          <ServerIcon className="h-5 w-5 mr-2 text-white" />
                        </div>
                        <Text size="2xl" className="text-white font-bold mb-1">
                          {serverStatus === 'ok' ? 'OK' : serverStatus === 'checking' ? '...' : serverStatus === 'cors' ? 'CORS' : serverStatus === null ? '—' : 'ERRO'}
                        </Text>
                        <div className="flex items-center justify-center gap-1">
                          <a 
                            href="https://dashboardapi.portalpasi.com.br/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-white hover:opacity-90 underline"
                          >
                            <Text size="xs" className="text-white">
                              Health da API (iaapi)
                            </Text>
                          </a>
                          <InfoTip content={HELP_API_HEALTH} label="Como interpretar o health da API" variant="onColor" position="bottom" />
                        </div>
                        {serverStatus === 'cors' && (
                          <Text size="xs" className="text-white opacity-75 mt-1">
                            Restrição de CORS
                          </Text>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Seção de Indicadores do Supabase */}
            <div className="px-6 py-6 border-t border-gray-200 dark:border-gray-700">
              <div className="mb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                    Indicadores do Supabase
                  </Heading>
                  <InfoTip content={HELP_SUPABASE_SECAO} label="Como interpretar Postgres e Storage" position="bottom" />
                  <button
                    type="button"
                    onClick={() => copyJsonToClipboard(buildSupabaseJson(), setCopiedSupabase)}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title="Copiar dados da seção como JSON"
                  >
                    {copiedSupabase ? (
                      <>
                        <CheckIcon className="h-4 w-4 text-green-500" />
                        <span className="text-green-600 dark:text-green-400">Copiado!</span>
                      </>
                    ) : (
                      <>
                        <ClipboardDocumentIcon className="h-4 w-4" />
                        <span>Copiar JSON</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadJsonFile(buildSupabaseJson(), 'indicadores-supabase')}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title="Baixar dados da seção como arquivo JSON"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    <span>Baixar JSON</span>
                  </button>
                  <a
                    href="https://status.supabase.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title="Abrir página oficial de status do Supabase"
                  >
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                    <span>Status do Supabase</span>
                  </a>
                </div>
              </div>

              {supabaseIndicatorsLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3">
                    <div className="text-center">
                      <div className="animate-pulse">
                        <div className="h-4 w-24 bg-gray-300 dark:bg-gray-600 rounded mx-auto mb-1"></div>
                        <div className="h-2 w-40 bg-gray-300 dark:bg-gray-600 rounded mx-auto"></div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3">
                    <div className="text-center">
                      <div className="animate-pulse">
                        <div className="h-4 w-24 bg-gray-300 dark:bg-gray-600 rounded mx-auto mb-1"></div>
                        <div className="h-2 w-40 bg-gray-300 dark:bg-gray-600 rounded mx-auto"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {supabaseIndicatorsError && (
                    <div className="mb-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                      <Text size="sm" className="text-yellow-800 dark:text-yellow-200">
                        {supabaseIndicatorsError}
                      </Text>
                    </div>
                  )}
                  
                  {/* Card de Tamanho do Banco de Dados */}
                  <div className="mb-6">
                    <div className="bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg rounded-lg p-3 text-white">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-start">
                          <ServerIcon className="h-6 w-6 mr-3 text-white shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <Text size="xs" className="text-white opacity-90 mb-0">
                                Postgres — disco (todos os bancos da instância)
                              </Text>
                              <InfoTip content={HELP_POSTGRES_DISCO_TOTAL} label="Origem deste tamanho" variant="onColor" position="bottom" />
                            </div>
                            <Text size="2xl" className="text-white font-bold">
                              {formatSize(databaseSize)} / 8 GB
                            </Text>
                          </div>
                        </div>
                      </div>
                      
                      {/* Barra de Progresso */}
                      <div className="mt-2">
                        <div className="flex items-center justify-between mb-1">
                          <Text size="xs" className="text-white opacity-90">
                            Capacidade utilizada (cota 8 GB — Postgres)
                          </Text>
                          <Text size="xs" className="text-white opacity-90 font-semibold">
                            {getDatabaseUsagePercentage().toFixed(1)}%
                          </Text>
                        </div>
                        <div className="w-full bg-white/20 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full transition-all duration-500 ${getProgressBarColor(getDatabaseUsagePercentage())}`}
                            style={{ width: `${getDatabaseUsagePercentage()}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Detalhe do catálogo Postgres (via RPC) — expansível como Buckets */}
                  <div className="space-y-4 mb-6">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-start min-w-0">
                            <ServerIcon className="h-6 w-6 mr-2 text-gray-600 dark:text-gray-400 shrink-0 mt-0.5" />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                                  Banco da conexão atual (current_database)
                                </Heading>
                                <InfoTip content={HELP_BANCO_CONEXAO} label="Diferença em relação ao card azul" position="bottom" />
                              </div>
                              <Text size="xs" color="muted" className="mt-1 font-mono break-all">
                                SELECT pg_size_pretty(pg_database_size(current_database())) AS tamanho_banco;
                              </Text>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 shrink-0">
                            {databaseSizePretty != null && (
                              <Text size="sm" className="text-gray-900 dark:text-white font-semibold whitespace-nowrap">
                                {databaseSizePretty}
                              </Text>
                            )}
                            <button
                              type="button"
                              onClick={() => setDbPrettyExpanded(!dbPrettyExpanded)}
                              className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
                            >
                              {dbPrettyExpanded ? (
                                <>
                                  <ChevronUpIcon className="h-5 w-5" />
                                  Ocultar
                                </>
                              ) : (
                                <>
                                  <ChevronDownIcon className="h-5 w-5" />
                                  Ver detalhes
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                      {dbPrettyExpanded && (
                        <div className="px-6 py-4">
                          <Text size="lg" className="text-gray-900 dark:text-white font-semibold">
                            {databaseSizePretty ?? '—'}
                          </Text>
                        </div>
                      )}
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-start min-w-0">
                            <TableCellsIcon className="h-6 w-6 mr-2 text-gray-600 dark:text-gray-400 shrink-0 mt-0.5" />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                                  Tabelas usuário — maior ocupação no Postgres
                                </Heading>
                                <InfoTip content={HELP_TABELAS_TAMANHO} label="O que a lista de tabelas mostra" position="bottom" />
                              </div>
                              <Text size="xs" color="muted" className="mt-1 font-mono break-all">
                                SELECT relname AS tabela, pg_size_pretty(pg_total_relation_size(relid)) AS tamanho FROM
                                pg_catalog.pg_statio_user_tables ORDER BY pg_total_relation_size(relid) DESC;
                              </Text>
                            </div>
                          </div>
                          {userTablesSizeRows.length > 0 && (
                            <div className="flex items-center gap-4 shrink-0">
                              <Text size="sm" className="text-gray-900 dark:text-white font-semibold whitespace-nowrap">
                                {userTablesSizeRows.length} tabela(s)
                              </Text>
                              <button
                                type="button"
                                onClick={() => setUserTablesExpanded(!userTablesExpanded)}
                                className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
                              >
                                {userTablesExpanded ? (
                                  <>
                                    <ChevronUpIcon className="h-5 w-5" />
                                    Ocultar
                                  </>
                                ) : (
                                  <>
                                    <ChevronDownIcon className="h-5 w-5" />
                                    Ver todos ({userTablesSizeRows.length})
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      {userTablesExpanded && (
                        <div className="overflow-x-auto">
                          {userTablesSizeRows.length === 0 ? (
                            <div className="px-6 py-8 text-center">
                              <Text size="sm" color="muted">
                                Nenhuma tabela retornada ou sem permissão para listar
                              </Text>
                            </div>
                          ) : (
                            <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                              <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    tabela
                                  </th>
                                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    tamanho
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {userTablesSizeRows.map((row, index) => (
                                  <tr key={`${row.tabela}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <Text size="sm" className="text-gray-900 dark:text-white font-mono">
                                        {row.tabela}
                                      </Text>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                      <Text size="sm" className="text-gray-900 dark:text-white font-semibold">
                                        {row.tamanho}
                                      </Text>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Lista de Buckets */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start min-w-0">
                          <FolderIcon className="h-6 w-6 mr-2 text-gray-600 dark:text-gray-400 shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                                Storage — tamanho por bucket
                              </Heading>
                              <InfoTip content={HELP_BUCKETS} label="Storage vs tamanho do Postgres" position="bottom" />
                            </div>
                          </div>
                        </div>
                        {bucketsSize.length > 0 && (
                          <div className="flex items-center gap-4 shrink-0">
                            <Text size="sm" className="text-gray-900 dark:text-white font-semibold">
                              Total: {formatSize(bucketsSize.reduce((sum, bucket) => sum + bucket.total_size_mb, 0))}
                            </Text>
                            <button
                              onClick={() => setBucketsExpanded(!bucketsExpanded)}
                              className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
                            >
                              {bucketsExpanded ? (
                                <>
                                  <ChevronUpIcon className="h-5 w-5" />
                                  Ocultar
                                </>
                              ) : (
                                <>
                                  <ChevronDownIcon className="h-5 w-5" />
                                  Ver todos ({bucketsSize.length})
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    {bucketsExpanded && (
                      <div className="overflow-x-auto">
                        {bucketsSize.length === 0 ? (
                          <div className="px-6 py-8 text-center">
                            <Text size="sm" color="muted">
                              Nenhum bucket encontrado ou sem dados disponíveis
                            </Text>
                          </div>
                        ) : (
                          <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Bucket ID
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Tamanho
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {bucketsSize.map((bucket) => (
                                <tr key={bucket.bucket_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <Text size="sm" className="text-gray-900 dark:text-white font-mono">
                                      {bucket.bucket_id}
                                    </Text>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right">
                                    <Text size="sm" className="text-gray-900 dark:text-white font-semibold">
                                      {formatSize(bucket.total_size_mb)}
                                    </Text>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Desempenho do Postgres */}
            <div className="px-6 py-6 border-t border-gray-200 dark:border-gray-700">
              <div className="mb-4 flex items-center gap-2 flex-wrap">
                <BoltIcon className="h-7 w-7 text-amber-500 dark:text-amber-400 shrink-0" aria-hidden />
                <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                  Desempenho do Postgres
                </Heading>
                <InfoTip content={HELP_DESEMPENHO_SECAO} label="Sobre estas métricas" position="bottom" />
                <button
                  type="button"
                  onClick={() => copyJsonToClipboard(buildDesempenhoJson(), setCopiedDesempenho)}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="Copiar dados da seção como JSON"
                >
                  {copiedDesempenho ? (
                    <>
                      <CheckIcon className="h-4 w-4 text-green-500" />
                      <span className="text-green-600 dark:text-green-400">Copiado!</span>
                    </>
                  ) : (
                    <>
                      <ClipboardDocumentIcon className="h-4 w-4" />
                      <span>Copiar JSON</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => downloadJsonFile(buildDesempenhoJson(), 'desempenho-postgres')}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="Baixar dados da seção como arquivo JSON"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  <span>Baixar JSON</span>
                </button>
              </div>

              {performanceLoading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 h-24 animate-pulse" />
                  ))}
                </div>
              ) : performanceError ? (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <Text size="sm" className="text-yellow-800 dark:text-yellow-200">
                    {performanceError}
                  </Text>
                </div>
              ) : (
                <>
                  {(() => {
                    const perf = performanceSummary
                    const cachePct =
                      perf?.cache_hit_ratio_pct != null ? Number(perf.cache_hit_ratio_pct) : null
                    const numbackends = perf?.numbackends != null ? Number(perf.numbackends) : null
                    const maxConnections = readMaxConnections(perf)
                    const deadlocks = perf?.deadlocks != null ? Number(perf.deadlocks) : null
                    const tempFiles = perf?.temp_files != null ? Number(perf.temp_files) : null
                    const tempBytes = perf?.temp_bytes != null ? Number(perf.temp_bytes) : null
                    const commits = perf?.xact_commit != null ? Number(perf.xact_commit) : 0
                    const rollbacks = perf?.xact_rollback != null ? Number(perf.xact_rollback) : 0
                    const totalTx = commits + rollbacks
                    const rollbackSharePct = totalTx > 0 ? (100 * rollbacks) / totalTx : null
                    return (
                      <>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                          <div
                            className={`bg-gradient-to-br shadow-lg rounded-lg p-3 text-white ${getCacheHitCardGradient(cachePct)}`}
                          >
                            <div className="flex items-start justify-between gap-1 mb-0.5">
                              <Text size="xs" className="text-white/90">
                                Cache (hit ratio)
                              </Text>
                              <InfoTip
                                content={HELP_CACHE_HIT}
                                label="Cache buffer"
                                variant="onColor"
                                position="bottom"
                              />
                            </div>
                            <Text size="2xl" className="text-white font-bold leading-tight">
                              {cachePct != null ? `${cachePct.toFixed(1)}%` : '—'}
                            </Text>
                          </div>

                          <div className="bg-gradient-to-br from-slate-500 to-slate-600 shadow-lg rounded-lg p-3 text-white">
                            <div className="flex items-start justify-between gap-1 mb-0.5">
                              <Text size="xs" className="text-white/90">
                                Conexões (atual / limite)
                              </Text>
                              <InfoTip
                                content={HELP_CONEXOES}
                                label="Conexões ao banco"
                                variant="onColor"
                                position="bottom"
                              />
                            </div>
                            <Text size="2xl" className="text-white font-bold leading-tight">
                              {numbackends != null && maxConnections != null
                                ? `${numbackends}/${maxConnections}`
                                : numbackends != null
                                  ? `${numbackends}/—`
                                  : '—'}
                            </Text>
                          </div>

                          <div
                            className={`bg-gradient-to-br shadow-lg rounded-lg p-3 text-white ${
                              deadlocks != null && deadlocks > 0
                                ? 'from-red-500 to-red-600'
                                : 'from-green-500 to-green-600'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-1 mb-0.5">
                              <Text size="xs" className="text-white/90">
                                Deadlocks
                              </Text>
                              <InfoTip
                                content={HELP_DEADLOCKS}
                                label="Deadlocks"
                                variant="onColor"
                                position="bottom"
                              />
                            </div>
                            <Text size="2xl" className="text-white font-bold leading-tight">
                              {deadlocks != null ? deadlocks : '—'}
                            </Text>
                          </div>

                          <div className="bg-gradient-to-br from-violet-500 to-violet-600 shadow-lg rounded-lg p-3 text-white">
                            <div className="flex items-start justify-between gap-1 mb-0.5">
                              <Text size="xs" className="text-white/90">
                                Temp em disco
                              </Text>
                              <InfoTip
                                content={HELP_TEMP_IO}
                                label="Arquivos temporários"
                                variant="onColor"
                                position="bottom"
                              />
                            </div>
                            <Text size="2xl" className="text-white font-bold leading-tight">
                              {tempBytes != null && tempBytes > 0 ? formatMemory(tempBytes) : '0 B'}
                            </Text>
                            <Text size="xs" className="text-white/80 mt-0.5">
                              {tempFiles != null ? `${tempFiles} arquivo(s)` : '—'}
                            </Text>
                          </div>

                          <div className="bg-gradient-to-br from-teal-500 to-teal-600 shadow-lg rounded-lg p-3 text-white md:col-span-3 lg:col-span-2">
                            <div className="flex items-start justify-between gap-1 mb-0.5">
                              <Text size="xs" className="text-white/90">
                                Transações (cumulativo)
                              </Text>
                              <InfoTip
                                content={HELP_TRANSACOES}
                                label="Commits e rollbacks"
                                variant="onColor"
                                position="bottom"
                              />
                            </div>
                            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                              <Text size="2xl" className="text-white font-bold leading-tight">
                                {commits.toLocaleString('pt-BR')} commits
                              </Text>
                              <Text size="sm" className="text-white/90">
                                {rollbacks.toLocaleString('pt-BR')} rollback
                                {rollbackSharePct != null && totalTx > 0
                                  ? ` (${rollbackSharePct.toFixed(2)}% rollback)`
                                  : ''}
                              </Text>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                          <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 shadow-lg rounded-lg p-3 text-white">
                            <div className="flex items-start justify-between gap-1 mb-0.5">
                              <Text size="xs" className="text-white/90">
                                TPS (intervalo)
                              </Text>
                              <InfoTip
                                content={HELP_TENDENCIA_INTERVALO}
                                label="TPS por intervalo"
                                variant="onColor"
                                position="bottom"
                              />
                            </div>
                            <Text size="2xl" className="text-white font-bold leading-tight">
                              {performanceInterval?.has_enough_points
                                ? Number(performanceInterval?.tps ?? 0).toFixed(2)
                                : '—'}
                            </Text>
                            <Text size="xs" className="text-white/80 mt-0.5">
                              Janela: {performanceInterval?.window_minutes ?? 15} min
                            </Text>
                          </div>

                          <div className="bg-gradient-to-br from-rose-500 to-rose-600 shadow-lg rounded-lg p-3 text-white">
                            <div className="flex items-start justify-between gap-1 mb-0.5">
                              <Text size="xs" className="text-white/90">
                                Rollbacks / min
                              </Text>
                              <InfoTip
                                content={HELP_TENDENCIA_INTERVALO}
                                label="Rollbacks por minuto"
                                variant="onColor"
                                position="bottom"
                              />
                            </div>
                            <Text size="2xl" className="text-white font-bold leading-tight">
                              {performanceInterval?.has_enough_points
                                ? Number(performanceInterval?.rollbacks_per_min ?? 0).toFixed(3)
                                : '—'}
                            </Text>
                            <Text size="xs" className="text-white/80 mt-0.5">
                              Deadlocks/min: {performanceInterval?.has_enough_points
                                ? Number(performanceInterval?.deadlocks_per_min ?? 0).toFixed(4)
                                : '—'}
                            </Text>
                          </div>

                          <div className="bg-gradient-to-br from-fuchsia-500 to-fuchsia-600 shadow-lg rounded-lg p-3 text-white">
                            <div className="flex items-start justify-between gap-1 mb-0.5">
                              <Text size="xs" className="text-white/90">
                                Temp MB / min
                              </Text>
                              <InfoTip
                                content={HELP_TENDENCIA_INTERVALO}
                                label="I/O temporário por minuto"
                                variant="onColor"
                                position="bottom"
                              />
                            </div>
                            <Text size="2xl" className="text-white font-bold leading-tight">
                              {performanceInterval?.has_enough_points
                                ? Number(performanceInterval?.temp_mb_per_min ?? 0).toFixed(3)
                                : '—'}
                            </Text>
                            <Text size="xs" className="text-white/80 mt-0.5">
                              {performanceInterval?.message || 'Delta por snapshots recentes'}
                            </Text>
                          </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-4">
                          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start min-w-0">
                                <BoltIcon className="h-6 w-6 mr-2 text-indigo-500 shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                                      Tendência por intervalo
                                    </Heading>
                                    <InfoTip content={HELP_TENDENCIA_INTERVALO} label="Intervalo" position="bottom" />
                                  </div>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => setPerfIntervalExpanded(!perfIntervalExpanded)}
                                className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors shrink-0"
                              >
                                {perfIntervalExpanded ? (
                                  <>
                                    <ChevronUpIcon className="h-5 w-5" />
                                    Ocultar
                                  </>
                                ) : (
                                  <>
                                    <ChevronDownIcon className="h-5 w-5" />
                                    Ver detalhes
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                          {perfIntervalExpanded && (
                            <div className="px-6 py-4">
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-mono">
                                from: {performanceInterval?.from ?? '—'} | to: {performanceInterval?.to ?? '—'} | segundos: {performanceInterval?.seconds_between ?? '—'}
                              </Text>
                              {!performanceInterval?.has_enough_points && (
                                <Text size="sm" color="muted" className="mt-2">
                                  {performanceInterval?.message || 'Ainda sem pontos suficientes para cálculo de delta.'}
                                </Text>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-4">
                          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start min-w-0">
                                <TableCellsIcon className="h-6 w-6 mr-2 text-gray-600 dark:text-gray-400 shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                                      Saúde de manutenção (autovacuum/analyze)
                                    </Heading>
                                    <InfoTip content={HELP_MANUTENCAO} label="Saúde de manutenção" position="bottom" />
                                  </div>
                                </div>
                              </div>
                              {maintenanceRows.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setMaintenanceExpanded(!maintenanceExpanded)}
                                  className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors shrink-0"
                                >
                                  {maintenanceExpanded ? (
                                    <>
                                      <ChevronUpIcon className="h-5 w-5" />
                                      Ocultar
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDownIcon className="h-5 w-5" />
                                      Ver todos ({maintenanceRows.length})
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                          {maintenanceExpanded && (
                            <div className="overflow-x-auto">
                              {maintenanceRows.length === 0 ? (
                                <div className="px-6 py-8 text-center">
                                  <Text size="sm" color="muted">
                                    Sem tabelas com volume mínimo para avaliação no momento.
                                  </Text>
                                </div>
                              ) : (
                                <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                                  <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Tabela</th>
                                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Dead %</th>
                                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Dead tuples</th>
                                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Autovacuum (min)</th>
                                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Score</th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {maintenanceRows.map((row, idx) => (
                                      <tr key={`${row.schemaname}.${row.relname}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <td className="px-4 py-3">
                                          <Text size="sm" className="text-gray-900 dark:text-white font-mono">
                                            {row.schemaname}.{row.relname}
                                          </Text>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                          <Text size="sm" className="text-gray-900 dark:text-white">
                                            {row.dead_tuple_pct != null ? `${Number(row.dead_tuple_pct).toFixed(2)}%` : '—'}
                                          </Text>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                          <Text size="sm" className="text-gray-900 dark:text-white">
                                            {Number(row.n_dead_tup ?? 0).toLocaleString('pt-BR')}
                                          </Text>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                          <Text size="sm" className="text-gray-900 dark:text-white">
                                            {row.autovacuum_age_minutes != null ? Number(row.autovacuum_age_minutes).toFixed(1) : '—'}
                                          </Text>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                          <Text size="sm" className="text-gray-900 dark:text-white font-semibold">
                                            {row.maintenance_risk_score != null ? Number(row.maintenance_risk_score).toFixed(2) : '—'}
                                          </Text>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-4">
                          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start min-w-0">
                                <UserIcon className="h-6 w-6 mr-2 text-gray-600 dark:text-gray-400 shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                                      Conexões de risco
                                    </Heading>
                                    <InfoTip content={HELP_CONEXOES_RISCO} label="Conexões de risco" position="bottom" />
                                  </div>
                                  <Text size="sm" color="muted" className="mt-1">
                                    idle in transaction acima do limiar: {connectionRisks?.idle_in_transaction_over_threshold ?? 0} | active prolongado: {connectionRisks?.active_over_threshold ?? 0}
                                  </Text>
                                </div>
                              </div>
                              {connectionRiskTopRows.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setConnectionRisksExpanded(!connectionRisksExpanded)}
                                  className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors shrink-0"
                                >
                                  {connectionRisksExpanded ? (
                                    <>
                                      <ChevronUpIcon className="h-5 w-5" />
                                      Ocultar
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDownIcon className="h-5 w-5" />
                                      Ver top ({connectionRiskTopRows.length})
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                          {connectionRisksExpanded && (
                            <div className="overflow-x-auto">
                              {connectionRiskTopRows.length === 0 ? (
                                <div className="px-6 py-8 text-center">
                                  <Text size="sm" color="muted">
                                    Nenhuma transação ativa longa listada no momento.
                                  </Text>
                                </div>
                              ) : (
                                <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                                  <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">PID</th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Aplicação</th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Usuário</th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Estado</th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Idade tx</th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Wait</th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {connectionRiskTopRows.map((row, idx) => (
                                      <tr key={`${row.pid}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <td className="px-4 py-3"><Text size="sm" className="text-gray-900 dark:text-white font-mono">{row.pid}</Text></td>
                                        <td className="px-4 py-3"><Text size="sm" className="text-gray-900 dark:text-white font-mono">{row.application_name || '—'}</Text></td>
                                        <td className="px-4 py-3"><Text size="sm" className="text-gray-900 dark:text-white font-mono">{row.usename || '—'}</Text></td>
                                        <td className="px-4 py-3"><Text size="sm" className="text-gray-900 dark:text-white">{row.state || '—'}</Text></td>
                                        <td className="px-4 py-3"><Text size="sm" className="text-gray-900 dark:text-white font-mono">{row.xact_age || '—'}</Text></td>
                                        <td className="px-4 py-3"><Text size="sm" className="text-gray-900 dark:text-white font-mono">{row.wait_event_type || row.wait_event ? `${row.wait_event_type || '—'} / ${row.wait_event || '—'}` : '—'}</Text></td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-4">
                          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start min-w-0">
                                <LockClosedIcon className="h-6 w-6 mr-2 text-gray-600 dark:text-gray-400 shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                                      Deadlocks e espera por locks
                                    </Heading>
                                    <InfoTip content={HELP_DEADLOCKS_SECAO} label="Deadlocks e logs" position="bottom" />
                                  </div>
                                  <Text size="sm" color="muted" className="mt-1">
                                    O Postgres não lista cada deadlock nas catalogs; use os logs para o detalhe. Abaixo:
                                    bloqueios ativos neste banco.
                                  </Text>
                                  {lockBlockingError && (
                                    <Text size="xs" color="muted" className="mt-1">
                                      {lockBlockingError}
                                    </Text>
                                  )}
                                </div>
                              </div>
                              {lockBlockingRows.length > 0 && (
                                <div className="shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => setPerfLocksExpanded(!perfLocksExpanded)}
                                    className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
                                  >
                                    {perfLocksExpanded ? (
                                      <>
                                        <ChevronUpIcon className="h-5 w-5" />
                                        Ocultar
                                      </>
                                    ) : (
                                      <>
                                        <ChevronDownIcon className="h-5 w-5" />
                                        Ver bloqueios ({lockBlockingRows.length})
                                      </>
                                    )}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          {!lockBlockingError && (
                            <div className="px-6 py-3 bg-gray-50/80 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
                              <Text size="sm" color="muted">
                                {lockBlockingRows.length === 0
                                  ? 'Nenhuma sessão à espera de lock no banco atual neste momento.'
                                  : `${lockBlockingRows.length} par(es) bloqueado/bloqueador — use “Ver bloqueios” para ver PIDs, objeto e consultas.`}
                              </Text>
                            </div>
                          )}
                          {perfLocksExpanded && lockBlockingRows.length > 0 && (
                            <div className="overflow-x-auto">
                                <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 min-w-[56rem]">
                                  <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Bloqueado PID
                                      </th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Bloqueando PID
                                      </th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Objeto
                                      </th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Tipo / modo
                                      </th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Estados
                                      </th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Consulta bloqueada
                                      </th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Consulta bloqueando
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {lockBlockingRows.map((row, idx) => (
                                      <tr
                                        key={`${row.bloqueado_pid}-${row.bloqueando_pid}-${row.locktype}-${row.modo}-${idx}`}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-700"
                                      >
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <Text size="sm" className="text-gray-900 dark:text-white font-mono">
                                            {row.bloqueado_pid}
                                          </Text>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <Text size="sm" className="text-gray-900 dark:text-white font-mono">
                                            {row.bloqueando_pid}
                                          </Text>
                                        </td>
                                        <td className="px-4 py-3 max-w-[12rem]">
                                          <Text size="sm" className="text-gray-900 dark:text-white font-mono break-all">
                                            {row.relacao ?? '—'}
                                          </Text>
                                        </td>
                                        <td className="px-4 py-3 max-w-[10rem]">
                                          <Text size="sm" className="text-gray-900 dark:text-white font-mono text-xs">
                                            {row.locktype} / {row.modo}
                                          </Text>
                                        </td>
                                        <td className="px-4 py-3 max-w-[10rem]">
                                          <Text size="sm" className="text-gray-700 dark:text-gray-300 text-xs">
                                            {row.bloqueado_estado ?? '—'} → {row.bloqueando_estado ?? '—'}
                                          </Text>
                                        </td>
                                        <td className="px-4 py-3 max-w-xs align-top">
                                          <Text
                                            size="sm"
                                            className="text-gray-700 dark:text-gray-300 font-mono text-xs break-words"
                                          >
                                            {row.bloqueado_query ?? '—'}
                                          </Text>
                                        </td>
                                        <td className="px-4 py-3 max-w-xs align-top">
                                          <Text
                                            size="sm"
                                            className="text-gray-700 dark:text-gray-300 font-mono text-xs break-words"
                                          >
                                            {row.bloqueando_query ?? '—'}
                                          </Text>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                            </div>
                          )}
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-4">
                          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start min-w-0">
                                <UserIcon className="h-6 w-6 mr-2 text-gray-600 dark:text-gray-400 shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                                      Conexões
                                    </Heading>
                                    <InfoTip content={HELP_CONEXOES_DETALHE} label="Conexões detalhadas" position="bottom" />
                                  </div>
                                  {connectionsError && (
                                    <Text size="xs" color="muted" className="mt-1">
                                      {connectionsError}
                                    </Text>
                                  )}
                                </div>
                              </div>
                              {connectionRows.length > 0 && (
                                <div className="shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => setPerfConnectionsExpanded(!perfConnectionsExpanded)}
                                    className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
                                  >
                                    {perfConnectionsExpanded ? (
                                      <>
                                        <ChevronUpIcon className="h-5 w-5" />
                                        Ocultar
                                      </>
                                    ) : (
                                      <>
                                        <ChevronDownIcon className="h-5 w-5" />
                                        Ver todos ({connectionRows.length})
                                      </>
                                    )}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          {perfConnectionsExpanded && (
                            <div className="overflow-x-auto">
                              {connectionRows.length === 0 ? (
                                <div className="px-6 py-8 text-center">
                                  <Text size="sm" color="muted">
                                    {connectionsError ? 'Não foi possível carregar.' : 'Nenhuma conexão listada.'}
                                  </Text>
                                </div>
                              ) : (
                                <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                                  <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Origem
                                      </th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Aplicação
                                      </th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Usuário
                                      </th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Estado
                                      </th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Tempo conectado
                                      </th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Tempo no estado
                                      </th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Idade transação
                                      </th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Cliente
                                      </th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Wait event
                                      </th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Query (trecho)
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {connectionRows.map((row, idx) => (
                                      <tr key={`${row.pid}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <Text size="sm" className="text-gray-900 dark:text-white font-semibold">
                                            {row.origem || '—'}
                                          </Text>
                                        </td>
                                        <td className="px-4 py-3 max-w-[220px]">
                                          <Text size="sm" className="text-gray-900 dark:text-white font-mono break-all">
                                            {row.application_name || '—'}
                                          </Text>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <Text size="sm" className="text-gray-900 dark:text-white font-mono">
                                            {row.usename || '—'}
                                          </Text>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <Text size="sm" className="text-gray-900 dark:text-white">
                                            {row.state || '—'}
                                          </Text>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <Text size="sm" className="text-gray-900 dark:text-white font-mono">
                                            {row.connected_for || '—'}
                                          </Text>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <Text size="sm" className="text-gray-900 dark:text-white font-mono">
                                            {row.state_for || '—'}
                                          </Text>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <Text size="sm" className="text-gray-900 dark:text-white font-mono">
                                            {row.xact_age || '—'}
                                          </Text>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <Text size="sm" className="text-gray-900 dark:text-white font-mono">
                                            {row.client_addr || '—'}
                                          </Text>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <Text size="sm" className="text-gray-900 dark:text-white font-mono">
                                            {row.wait_event_type || row.wait_event
                                              ? `${row.wait_event_type || '—'} / ${row.wait_event || '—'}`
                                              : '—'}
                                          </Text>
                                        </td>
                                        <td className="px-4 py-3 max-w-[300px] align-top">
                                          <Text size="sm" className="text-gray-700 dark:text-gray-300 font-mono text-xs break-words">
                                            {row.query_preview || '—'}
                                          </Text>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-4">
                          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start min-w-0">
                                <CpuChipIcon className="h-6 w-6 mr-2 text-gray-600 dark:text-gray-400 shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                                      Sessões por estado
                                    </Heading>
                                    <InfoTip
                                      content={HELP_SESSOES_ESTADO}
                                      label="Sessões por estado"
                                      position="bottom"
                                    />
                                  </div>
                                  {activitySummaryError && (
                                    <Text size="xs" color="muted" className="mt-1">
                                      {activitySummaryError}
                                    </Text>
                                  )}
                                </div>
                              </div>
                              {activitySummaryRows.length > 0 && (
                                <div className="shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => setPerfActivityExpanded(!perfActivityExpanded)}
                                    className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
                                  >
                                    {perfActivityExpanded ? (
                                      <>
                                        <ChevronUpIcon className="h-5 w-5" />
                                        Ocultar
                                      </>
                                    ) : (
                                      <>
                                        <ChevronDownIcon className="h-5 w-5" />
                                        Ver todos ({activitySummaryRows.length})
                                      </>
                                    )}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          {perfActivityExpanded && (
                            <div className="overflow-x-auto">
                              {activitySummaryRows.length === 0 ? (
                                <div className="px-6 py-8 text-center">
                                  <Text size="sm" color="muted">
                                    {activitySummaryError ? 'Não foi possível carregar.' : 'Nenhuma sessão listada.'}
                                  </Text>
                                </div>
                              ) : (
                                <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                                  <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Estado
                                      </th>
                                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Quantidade
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {activitySummaryRows.map((row) => (
                                      <tr key={row.estado} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <td className="px-6 py-3">
                                          <Text size="sm" className="text-gray-900 dark:text-white font-mono">
                                            {row.estado}
                                          </Text>
                                        </td>
                                        <td className="px-6 py-3 text-right">
                                          <Text size="sm" className="text-gray-900 dark:text-white font-semibold">
                                            {Number(row.quantidade).toLocaleString('pt-BR')}
                                          </Text>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-4">
                          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start min-w-0">
                                <TableCellsIcon className="h-6 w-6 mr-2 text-gray-600 dark:text-gray-400 shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                                      Candidatas a sequential scan
                                    </Heading>
                                    <InfoTip content={HELP_SEQ_SCAN} label="Sequential scans" position="bottom" />
                                  </div>
                                </div>
                              </div>
                              {seqScanRows.length > 0 && (
                                <div className="shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => setPerfSeqExpanded(!perfSeqExpanded)}
                                    className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
                                  >
                                    {perfSeqExpanded ? (
                                      <>
                                        <ChevronUpIcon className="h-5 w-5" />
                                        Ocultar
                                      </>
                                    ) : (
                                      <>
                                        <ChevronDownIcon className="h-5 w-5" />
                                        Ver todos ({seqScanRows.length})
                                      </>
                                    )}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          {perfSeqExpanded && (
                            <div className="overflow-x-auto">
                              {seqScanRows.length === 0 ? (
                                <div className="px-6 py-8 text-center">
                                  <Text size="sm" color="muted">
                                    Nenhuma tabela atingiu o mínimo de scans ou sem dados.
                                  </Text>
                                </div>
                              ) : (
                                <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                                  <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Tabela
                                      </th>
                                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Seq scan
                                      </th>
                                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Index scan
                                      </th>
                                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        % seq
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {[...seqScanRows]
                                      .sort((a, b) => {
                                        const aPct = a.seq_scan_pct ?? -1
                                        const bPct = b.seq_scan_pct ?? -1
                                        if (bPct !== aPct) return bPct - aPct
                                        return Number(b.seq_scan) - Number(a.seq_scan)
                                      })
                                      .map((row, idx) => (
                                      <tr key={`${row.relname}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <td className="px-6 py-3">
                                          <Text size="sm" className="text-gray-900 dark:text-white font-mono">
                                            {row.relname}
                                          </Text>
                                        </td>
                                        <td className="px-6 py-3 text-right">
                                          <Text size="sm" className="text-gray-900 dark:text-white">
                                            {Number(row.seq_scan).toLocaleString('pt-BR')}
                                          </Text>
                                        </td>
                                        <td className="px-6 py-3 text-right">
                                          <Text size="sm" className="text-gray-900 dark:text-white">
                                            {Number(row.idx_scan).toLocaleString('pt-BR')}
                                          </Text>
                                        </td>
                                        <td className="px-6 py-3 text-right">
                                          <Text size="sm" className="text-gray-900 dark:text-white font-semibold">
                                            {row.seq_scan_pct != null ? `${Number(row.seq_scan_pct).toFixed(1)}%` : '—'}
                                          </Text>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start min-w-0">
                                <BoltIcon className="h-6 w-6 mr-2 text-amber-500 shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                                      Consultas mais pesadas (pg_stat_statements)
                                    </Heading>
                                    <InfoTip content={HELP_PG_STAT_STMT} label="pg_stat_statements" position="bottom" />
                                  </div>
                                </div>
                              </div>
                              {stmtRows.length > 0 && (
                                <div className="shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => setPerfStmtExpanded(!perfStmtExpanded)}
                                    className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
                                  >
                                    {perfStmtExpanded ? (
                                      <>
                                        <ChevronUpIcon className="h-5 w-5" />
                                        Ocultar
                                      </>
                                    ) : (
                                      <>
                                        <ChevronDownIcon className="h-5 w-5" />
                                        Ver todos ({stmtRows.length})
                                      </>
                                    )}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          {perfStmtExpanded && (
                            <div className="overflow-x-auto">
                              {stmtRows.length === 0 ? (
                                <div className="px-6 py-8 text-center space-y-2">
                                  <Text size="sm" color="muted">
                                    Nenhuma consulta listada. Habilite a extensão pg_stat_statements no painel do Supabase
                                    (Database → Extensions) e aguarde tráfego para acumular estatísticas.
                                  </Text>
                                </div>
                              ) : (
                                <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                                  <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Consulta (trecho)
                                      </th>
                                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Chamadas
                                      </th>
                                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Tempo total (ms)
                                      </th>
                                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Média (ms)
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {stmtRows.map((row, idx) => (
                                      <tr key={`${idx}-${row.query_preview.slice(0, 24)}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <td className="px-6 py-3 max-w-md">
                                          <Text size="sm" className="text-gray-900 dark:text-white font-mono break-all">
                                            {row.query_preview}
                                          </Text>
                                        </td>
                                        <td className="px-6 py-3 text-right whitespace-nowrap">
                                          <Text size="sm" className="text-gray-900 dark:text-white">
                                            {Number(row.calls).toLocaleString('pt-BR')}
                                          </Text>
                                        </td>
                                        <td className="px-6 py-3 text-right whitespace-nowrap">
                                          <Text size="sm" className="text-gray-900 dark:text-white font-semibold">
                                            {Number(row.total_exec_time_ms).toLocaleString('pt-BR', {
                                              maximumFractionDigits: 0,
                                            })}
                                          </Text>
                                        </td>
                                        <td className="px-6 py-3 text-right whitespace-nowrap">
                                          <Text size="sm" className="text-gray-900 dark:text-white">
                                            {Number(row.mean_exec_time_ms).toLocaleString('pt-BR', {
                                              maximumFractionDigits: 2,
                                            })}
                                          </Text>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          )}
                        </div>
                      </>
                    )
                  })()}
                </>
              )}
            </div>

            {/* Seção de Servidor 10.60.60.115 */}
            <div className="px-6 py-6 border-t border-gray-200 dark:border-gray-700">
              <div className="mb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                    Servidor 10.60.60.115
                  </Heading>
                  <InfoTip content={HELP_SERVIDOR_SECAO} label="O que esta seção mostra" position="bottom" />
                </div>
              </div>

              {serverInfoLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3">
                    <div className="text-center">
                      <div className="animate-pulse">
                        <div className="h-4 w-24 bg-gray-300 dark:bg-gray-600 rounded mx-auto mb-1"></div>
                        <div className="h-2 w-40 bg-gray-300 dark:bg-gray-600 rounded mx-auto"></div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3">
                    <div className="text-center">
                      <div className="animate-pulse">
                        <div className="h-4 w-24 bg-gray-300 dark:bg-gray-600 rounded mx-auto mb-1"></div>
                        <div className="h-2 w-40 bg-gray-300 dark:bg-gray-600 rounded mx-auto"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {serverInfoError && (
                    <div className="mb-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                      <Text size="sm" className="text-yellow-800 dark:text-yellow-200">
                        {serverInfoError}
                      </Text>
                    </div>
                  )}

                  {serverInfo && (
                    <>
                      {/* Cards de Resumo */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        {(() => {
                          const pm2Active = serverInfo.pm2.processes.filter(p => p.status.toLowerCase() === 'online').length
                          const pm2Total = serverInfo.pm2.total
                          const pm2HasInactive = pm2Active < pm2Total
                          return (
                            <div className={`bg-gradient-to-br shadow-lg rounded-lg p-3 text-white ${
                              pm2HasInactive 
                                ? 'from-red-500 to-red-600' 
                                : 'from-green-500 to-green-600'
                            }`}>
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="flex items-center gap-1">
                                    <Text size="xs" className="text-white opacity-90 mb-0">
                                      Processos PM2
                                    </Text>
                                    <InfoTip content={HELP_PM2_RESUMO} label="Como ler PM2 online/total" variant="onColor" position="bottom" />
                                  </div>
                                  <Text size="2xl" className="text-white font-bold">
                                    {pm2Active}/{pm2Total}
                                  </Text>
                                </div>
                                <ServerIcon className="h-8 w-8 text-white opacity-80" />
                              </div>
                            </div>
                          )
                        })()}
                        {(() => {
                          const nginxActive = serverInfo.nginx.projects.filter(p => p.status.toLowerCase() === 'active').length
                          const nginxTotal = serverInfo.nginx.total
                          const nginxHasInactive = nginxActive < nginxTotal
                          return (
                            <div className={`bg-gradient-to-br shadow-lg rounded-lg p-3 text-white ${
                              nginxHasInactive 
                                ? 'from-red-500 to-red-600' 
                                : 'from-green-500 to-green-600'
                            }`}>
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="flex items-center gap-1">
                                    <Text size="xs" className="text-white opacity-90 mb-0">
                                      Projetos Nginx
                                    </Text>
                                    <InfoTip content={HELP_NGINX_RESUMO} label="Como ler Nginx ativos/total" variant="onColor" position="bottom" />
                                  </div>
                                  <Text size="2xl" className="text-white font-bold">
                                    {nginxActive}/{nginxTotal}
                                  </Text>
                                </div>
                                <FolderIcon className="h-8 w-8 text-white opacity-80" />
                              </div>
                            </div>
                          )
                        })()}
                      </div>

                      {/* Tabela única de processos PM2 e Nginx */}
                      {(() => {
                        const serverProcesses = [
                          ...serverInfo.pm2.processes.map((process) => ({
                            tipo: 'PM2' as const,
                            key: `pm2-${process.name}`,
                            name: process.name,
                            status: process.status,
                            uptime: formatUptime(process.uptime),
                            memory: formatMemory(process.memory),
                            cpu: process.cpu,
                            restarts: process.restarts,
                            serverName: null as string | null,
                            port: null as number | null,
                            configFile: null as string | null,
                            root: null as string | null,
                          })),
                          ...serverInfo.nginx.projects.map((project) => ({
                            tipo: 'Nginx' as const,
                            key: `nginx-${project.name}-${project.port}`,
                            name: project.name,
                            status: project.status,
                            uptime: null as string | null,
                            memory: null as string | null,
                            cpu: null as number | null,
                            restarts: null as number | null,
                            serverName: project.serverName,
                            port: project.port,
                            configFile: project.configFile,
                            root: project.root,
                          })),
                        ]
                        const renderEmpty = () => (
                          <Text size="sm" color="muted">
                            —
                          </Text>
                        )

                        return (
                          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <ServerIcon className="h-6 w-6 mr-2 text-gray-600 dark:text-gray-400" />
                                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                                    Processos
                                  </Heading>
                                </div>
                                {serverProcesses.length > 0 && (
                                  <button
                                    onClick={() => setProcessesExpanded(!processesExpanded)}
                                    className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
                                  >
                                    {processesExpanded ? (
                                      <>
                                        <ChevronUpIcon className="h-5 w-5" />
                                        Ocultar
                                      </>
                                    ) : (
                                      <>
                                        <ChevronDownIcon className="h-5 w-5" />
                                        Ver todos ({serverProcesses.length})
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                            {processesExpanded && (
                              <div className="overflow-x-auto">
                                {serverProcesses.length === 0 ? (
                                  <div className="px-6 py-8 text-center">
                                    <Text size="sm" color="muted">
                                      Nenhum processo encontrado
                                    </Text>
                                  </div>
                                ) : (
                                  <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-700">
                                      <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                          Tipo
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                          Nome
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                          Status
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                          Uptime
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                          Memória
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                          CPU
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                          Reinicializações
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                          Server Name
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                          Porta
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                          Arquivo Config
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                          Root
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                      {serverProcesses.map((row) => (
                                        <tr key={row.key} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                          <td className="px-6 py-4 whitespace-nowrap">
                                            <span
                                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                row.tipo === 'PM2'
                                                  ? 'bg-sky-100 dark:bg-sky-900/30 text-sky-800 dark:text-sky-200'
                                                  : 'bg-violet-100 dark:bg-violet-900/30 text-violet-800 dark:text-violet-200'
                                              }`}
                                            >
                                              {row.tipo}
                                            </span>
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap">
                                            <Text size="sm" className="text-gray-900 dark:text-white font-mono font-semibold">
                                              {row.name}
                                            </Text>
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(row.status)}`}>
                                              {row.status}
                                            </span>
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-right">
                                            {row.uptime ? (
                                              <Text size="sm" className="text-gray-900 dark:text-white">
                                                {row.uptime}
                                              </Text>
                                            ) : (
                                              renderEmpty()
                                            )}
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-right">
                                            {row.memory ? (
                                              <Text size="sm" className="text-gray-900 dark:text-white font-semibold">
                                                {row.memory}
                                              </Text>
                                            ) : (
                                              renderEmpty()
                                            )}
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-right">
                                            {row.cpu !== null ? (
                                              <Text
                                                size="sm"
                                                className={`font-semibold ${
                                                  row.cpu > 80
                                                    ? 'text-red-600 dark:text-red-400'
                                                    : row.cpu > 50
                                                      ? 'text-yellow-600 dark:text-yellow-400'
                                                      : 'text-gray-900 dark:text-white'
                                                }`}
                                              >
                                                {row.cpu.toFixed(1)}%
                                              </Text>
                                            ) : (
                                              renderEmpty()
                                            )}
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-right">
                                            {row.restarts !== null ? (
                                              <Text
                                                size="sm"
                                                className={`font-semibold ${
                                                  row.restarts > 10
                                                    ? 'text-red-600 dark:text-red-400'
                                                    : row.restarts > 5
                                                      ? 'text-yellow-600 dark:text-yellow-400'
                                                      : 'text-gray-900 dark:text-white'
                                                }`}
                                              >
                                                {row.restarts}
                                              </Text>
                                            ) : (
                                              renderEmpty()
                                            )}
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap">
                                            {row.serverName ? (
                                              <Text size="sm" className="text-gray-900 dark:text-white font-mono">
                                                {row.serverName}
                                              </Text>
                                            ) : (
                                              renderEmpty()
                                            )}
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-right">
                                            {row.port !== null ? (
                                              <Text size="sm" className="text-gray-900 dark:text-white font-semibold">
                                                {row.port}
                                              </Text>
                                            ) : (
                                              renderEmpty()
                                            )}
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap">
                                            {row.configFile ? (
                                              <Text size="sm" className="text-gray-900 dark:text-white font-mono">
                                                {row.configFile}
                                              </Text>
                                            ) : (
                                              renderEmpty()
                                            )}
                                          </td>
                                          <td className="px-6 py-4">
                                            {row.root ? (
                                              <Text size="sm" className="text-gray-900 dark:text-white font-mono">
                                                {row.root}
                                              </Text>
                                            ) : (
                                              renderEmpty()
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            )}
                          </div>
                        )
                      })()}
                    </>
                  )}
                </>
              )}

              <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <BoltIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                    <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                      Diagnóstico de IA no Servidor Linux
                    </Heading>
                  </div>
                  <Button
                    onClick={handleServerDiagnosticWebhook}
                    disabled={serverDiagLoading}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <ArrowPathIcon className={`h-5 w-5 ${serverDiagLoading ? 'animate-spin' : ''}`} />
                    {serverDiagLoading ? 'Analisando…' : 'Executar diagnóstico'}
                  </Button>
                </div>
                {serverDiagExecuted && (
                  <div className="px-6 py-4 space-y-3">
                    {serverDiagError && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                        <Text size="sm" className="text-yellow-800 dark:text-yellow-200">
                          {serverDiagError}
                        </Text>
                      </div>
                    )}
                    <textarea
                      readOnly
                      aria-label="Resultado do diagnóstico de IA"
                      rows={18}
                      value={serverDiagOutput ?? ''}
                      placeholder={
                        serverDiagLoading ? 'Aguardando resposta do webhook…' : ''
                      }
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-mono text-sm p-3 whitespace-pre-wrap resize-y min-h-[12rem] focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </PermissionGuard>
    </div>
  )
}

export default ConfiguracoesMonitoramento
